import { Button } from "@/design-system/components/ui";
import { usePortalAuth } from "../PortalAuthContext";

// Disable / Re-enable / Archive — the full-page replacement shown to a portal
// user whose account has been disabled by staff. Two entry points:
//   • PortalChrome (already-authed): pass structured `email` / `phone` from
//     the 200 /auth/me response so the contact renders as a mailto link with
//     the phone on a second line.
//   • BrokerLoginPage (login 403): pass the server-built `message` string
//     (it already inlines the support contact); no portal session exists.
// No sidebar, no top bar, no portal navigation — by design.

interface AccountDisabledPageProps {
  email?: string | null;
  phone?: string | null;
  /** Pre-built sentence used by the login path (overrides email/phone copy). */
  message?: string | null;
}

export function AccountDisabledPage({ email, phone, message }: AccountDisabledPageProps) {
  const auth = usePortalAuth();
  const signedIn = !!auth.me;

  async function handleAction() {
    if (signedIn) {
      await auth.logout();
    }
    if (typeof window !== "undefined") {
      window.location.assign("/portal/login");
    }
  }

  return (
    <div className="min-h-screen bg-white-warm text-onyx flex items-center justify-center px-space-4">
      <div className="w-full max-w-md rounded-lg border border-border-light bg-white-pure p-space-6 shadow-sm text-center">
        <h1 className="text-h2 font-display text-onyx m-0">Account disabled</h1>

        {message ? (
          <p className="text-small text-onyx mt-space-4">{message}</p>
        ) : (
          <>
            <p className="text-small text-onyx mt-space-4">
              Unfortunately, your account has been disabled. If you have questions
              please contact{" "}
              {email ? (
                <a href={`mailto:${email}`} className="underline text-gold-shadow">
                  {email}
                </a>
              ) : (
                "our support team"
              )}
              .
            </p>
            {phone && (
              <p className="text-small text-onyx mt-space-2">Or call {phone}.</p>
            )}
          </>
        )}

        <div className="mt-space-5">
          <Button variant="secondary" size="sm" type="button" onClick={() => void handleAction()}>
            {signedIn ? "Sign out" : "Back to sign in"}
          </Button>
        </div>
      </div>
    </div>
  );
}
